// Functions that change the store.
// They get a GetFunction and a SetFunction as arguments, which they can use to read and update the store state.

// These should be tested! If there are no bugs here, then the store will stay consistent.

import {
  MoveDirection,
  moveByDirection,
  randomUUID,
  remove,
  replaceElement,
} from "@nens/shared-utils";

import { CompleteFile } from "../types/file-reference-with-file";
import { Folder, Layer, PublicationMap } from "../types/publication";
import { getDataTypeFromFile } from "../utils/get-data-type-from-file";

import { getPropertiesFromCompleteFile } from "./layer-property-converters";
import { getIsChanged, getMaps, getOpenMap } from "./selectors";
import { GetFunction, SetFunction } from "./store";

const createNewLayer = (
  fileReference: CompleteFile,
  layerName: string,
  layerInFile?: string
): Layer => ({
  type: "layer",
  id: randomUUID(),
  filePath: fileReference.path,
  name: layerName,
  layerInFile: layerInFile ?? null,
  visible: true,
  selected: false,
  opacity: 1,
  properties: getPropertiesFromCompleteFile(fileReference),
  dataType: getDataTypeFromFile(fileReference.file),
});

export const createAddLayerToCurrentMap =
  (get: GetFunction, set: SetFunction) =>
  (fileReference: CompleteFile, layerName: string, layerInFile?: string) => {
    // Add layer to the currently open map, using the file reference to get the filePath for the layer, and the layer name for the layer name. Also mark the publication as changed.
    const currentMap = getOpenMap(get());
    if (!currentMap) {
      console.warn("No open map to add layer to");
      return;
    }

    // Check that fileReference exists in the publication
    const publicationFiles = get().files;
    const fileExists = publicationFiles.some((file) => file.path === fileReference.path);

    if (!fileExists) {
      console.warn("File reference does not exist in publication");
      return;
    }

    const newMap: PublicationMap = {
      ...currentMap,
      layers: [...currentMap.layers, createNewLayer(fileReference, layerName, layerInFile)],
    };

    // Don't use get().replaceMap() because replaceMap doesn't exist in the mock store that we use for testing, and we don't want to add it there just for this test. Instead, we will manually replace the map in the maps array and then set the new maps array in the store.
    const newMaps = get().maps.map((map) => (map.id === currentMap.id ? newMap : map));
    set({ maps: newMaps });
  };

export const createSetStudyAreaForCurrentMap =
  (get: GetFunction, set: SetFunction) =>
  (fileReference: CompleteFile, layerName: string, layerInFile?: string) => {
    const currentMap = getOpenMap(get());
    if (!currentMap) {
      console.warn("No open map to set study area for");
      return;
    }

    const publicationFiles = get().files;
    const fileExists = publicationFiles.some((file) => file.path === fileReference.path);

    if (!fileExists) {
      console.warn("File reference does not exist in publication");
      return;
    }

    const updatedMap: PublicationMap = {
      ...currentMap,
      studyArea: createNewLayer(fileReference, layerName, layerInFile),
    };

    const newMaps = get().maps.map((map) => (map.id === currentMap.id ? updatedMap : map));
    set({ maps: newMaps });
  };

export const createRemoveStudyAreaFromCurrentMap = (get: GetFunction, set: SetFunction) => () => {
  const currentMap = getOpenMap(get());
  if (!currentMap) {
    console.warn("No open map to remove study area from");
    return;
  }

  const updatedMap: PublicationMap = {
    ...currentMap,
    studyArea: null,
  };

  const newMaps = get().maps.map((map) => (map.id === currentMap.id ? updatedMap : map));
  set({ maps: newMaps });
};

export const createToggleStudyAreaVisibilityForCurrentMap =
  (get: GetFunction, set: SetFunction) => () => {
    const currentMap = getOpenMap(get());
    if (!currentMap?.studyArea) {
      return;
    }

    const updatedMap: PublicationMap = {
      ...currentMap,
      studyArea: {
        ...currentMap.studyArea,
        visible: !currentMap.studyArea.visible,
      },
    };

    const newMaps = get().maps.map((map) => (map.id === currentMap.id ? updatedMap : map));
    set({ maps: newMaps });
  };

export const createMoveMap =
  (get: GetFunction, set: SetFunction) => (mapId: string, direction: MoveDirection) => {
    const maps = getMaps(get());
    const idx = maps.findIndex((map) => map.id === mapId);
    if (idx !== -1) {
      set({
        maps: moveByDirection(maps, idx, direction),
      });
    }
  };

export const createAddNewMap = (get: GetFunction, set: SetFunction) => (name: string) => {
  const maps = get().maps;
  const newMap: PublicationMap = {
    // No mapId property, that is created by the backend after saving
    id: randomUUID(),
    name,
    layers: [],
    mapId: null,
    studyArea: null, // New maps start with no study area, the user can add one later
  };
  set({ maps: [...maps, newMap], openMap: newMap.id });
};

export const createSetMapName =
  (get: GetFunction, set: SetFunction) => (mapId: string, name: string) => {
    const maps = get().maps;
    const mapIndex = maps.findIndex((map) => map.id === mapId);
    if (mapIndex === -1) {
      console.warn(`Map with id ${mapId} not found`);
      return;
    }
    const updatedMap = { ...maps[mapIndex], name };
    const updatedMaps = [...maps];
    updatedMaps[mapIndex] = updatedMap;
    set({ maps: updatedMaps });
  };

export const createDeleteMap = (get: GetFunction, set: SetFunction) => (mapId: string) => {
  const maps = get().maps;
  const mapIndex = maps.findIndex((map) => map.id === mapId);
  if (mapIndex === -1) {
    console.warn(`Map with id ${mapId} not found`);
    return;
  }
  if (maps.length === 1) {
    console.warn(`Cannot delete the only map`);
    return;
  }
  const updatedMaps = maps.filter((map) => map.id !== mapId);
  const openMap = get().openMap === mapId ? updatedMaps[0].id : get().openMap;
  set({ maps: updatedMaps, openMap });
};

export const createReplaceMap = (get: GetFunction, set: SetFunction) => (map: PublicationMap) => {
  const maps = get().maps;
  const mapIndex = maps.findIndex((m) => m.id === map.id);
  if (mapIndex === -1) {
    console.warn(`Map with id ${map.id} not found`);
    return;
  }
  const updatedMaps = [...maps];
  updatedMaps[mapIndex] = map;
  set({ maps: updatedMaps });
};

export const createToggleLayerVisibility = (get: GetFunction) => (layer: Layer) => {
  const updatedLayer: Layer = {
    ...layer,
    visible: !layer.visible,
  };
  get().replaceLayer(updatedLayer);
};

// ─── Layer selection helpers ────────────────────────────────────────────────

// Build a flat ordered list of all layers
const getAllLayers = (layers: PublicationMap["layers"]): Layer[] =>
  layers.flatMap((layer) => (layer.type === "folder" ? getAllLayers(layer.layers) : [layer]));

/**
 * Returns a copy of the top-level layers array with each layer's `selected`
 * flag replaced by the result of `selectFn(layer.id)`.
 * Works for both bare Layer items and Folder items (applying the same
 * function to every sublayer inside the folder).
 */
function applySelectionFuncToLayers(
  mapLayers: PublicationMap["layers"],
  selectFn: (id: string) => boolean
): PublicationMap["layers"] {
  return mapLayers.map((item) => {
    if (item.type === "layer") {
      return { ...item, selected: selectFn(item.id) };
    }
    // Folder: propagate selection to every sublayer
    return {
      ...item,
      layers: item.layers.map((sublayer) => ({ ...sublayer, selected: selectFn(sublayer.id) })),
    };
  });
}

export const createToggleLayerSelected = (get: GetFunction) => (layer: Layer) => {
  const updatedLayer: Layer = {
    ...layer,
    selected: !layer.selected,
  };
  get().replaceLayer(updatedLayer);
};

/**
 * Exclusively selects a single layer.
 * If the layer is already the only selected layer, clicking it again deselects
 * everything (so the user can return to the "show all" state).
 */
export const createSelectLayer = (get: GetFunction) => (layer: Layer) => {
  const publication = get();
  const openMap = getOpenMap(publication);

  const flatLayers = getAllLayers(openMap.layers);
  const currentlySelectedIds = flatLayers.filter((l) => l.selected).map((l) => l.id);
  const isOnlySelected = currentlySelectedIds.length === 1 && currentlySelectedIds[0] === layer.id;

  // If this is already the only selected item, clicking it again deselects all
  const updatedLayers = applySelectionFuncToLayers(
    openMap.layers,
    (id) => !isOnlySelected && id === layer.id
  );

  publication.replaceMap({ ...openMap, layers: updatedLayers });
};

/**
 * Additively selects all layers between (and including) the anchor layer and
 * the target layer, keeping any already-selected layers selected.
 * Mirrors Figma / Google Drive shift-click behaviour.
 */
export const createSelectLayerRange =
  (get: GetFunction) => (anchorId: string, targetId: string) => {
    const publication = get();
    const openMap = getOpenMap(publication);

    const flatLayers: Layer[] = getAllLayers(openMap.layers);

    const anchorIndex = flatLayers.findIndex((l) => l.id === anchorId);
    const targetIndex = flatLayers.findIndex((l) => l.id === targetId);

    if (anchorIndex === -1 || targetIndex === -1) return;

    const rangeStart = Math.min(anchorIndex, targetIndex);
    const rangeEnd = Math.max(anchorIndex, targetIndex);
    const rangeIds = new Set(flatLayers.slice(rangeStart, rangeEnd + 1).map((l) => l.id));

    // A layer is selected if it was already selected OR if it falls within the new range
    const updatedLayers = applySelectionFuncToLayers(
      openMap.layers,
      (id) => rangeIds.has(id) || !!flatLayers.find((l) => l.id === id)?.selected
    );
    publication.replaceMap({ ...openMap, layers: updatedLayers });
  };

/**
 * Clears the selection on all layers.
 */
export const createDeselectAllLayers = (get: GetFunction) => () => {
  const publication = get();
  const openMap = getOpenMap(publication);
  const updatedLayers = applySelectionFuncToLayers(openMap.layers, () => false);
  publication.replaceMap({ ...openMap, layers: updatedLayers });
};

export const createToggleFolderOpen = (get: GetFunction) => (folder: Folder) => {
  get().replaceLayer({
    ...folder,
    opened: !folder.opened,
  });
};

export const createAddPublicationFiles = (set: SetFunction) => (files: CompleteFile[]) =>
  set((state) => ({
    files: [...state.files, ...files],
  }));

export const createAddFolder = (get: GetFunction) => (name: string) => {
  const publication = get();
  const openMap = getOpenMap(publication);
  const newFolder: Folder = {
    id: randomUUID(),
    type: "folder",
    name,
    layers: [],
    opened: true,
  };
  publication.replaceMap({
    ...openMap,
    layers: [...openMap.layers, newFolder],
  });
};

export const createRemoveLayerFromFolder = (get: GetFunction) => (folder: Folder, layer: Layer) => {
  const publication = get();
  const openMap = getOpenMap(publication);

  // Add layer to the end of the map
  publication.replaceMap({
    ...openMap,
    layers: [...openMap.layers, layer],
  });

  // Remove it from the folder
  const idx = folder.layers.findIndex((sublayer) => sublayer.id === layer.id);
  if (idx !== -1) {
    // Re-get publication because it has changed
    get().replaceLayer({
      ...folder,
      layers: remove(folder.layers, idx),
    });
  }
};

export const createAddLayerToFolder = (get: GetFunction) => (folder: Folder, layer: Layer) => {
  // Remove layer from map
  get().removeLayer(layer.id);
  // And then add the layer to the folder
  get().replaceLayer({
    ...folder,
    layers: [...folder.layers, layer],
  });
};

export const createRemoveLayer = (get: GetFunction) => (layerId: string) => {
  const openMap = getOpenMap(get());
  const idx = openMap.layers.findIndex((layer) => layer.id === layerId);
  if (idx !== -1) {
    /* The layer is at root level, remove it from the layers array */
    get().replaceMap({
      ...openMap,
      layers: remove(openMap.layers, idx),
    });
  } else {
    /* The layer is in a folder, find the group and remove it from that */
    const folder = openMap.layers.find(
      (layer): layer is Folder =>
        layer.type === "folder" && layer.layers.some((sublayer) => sublayer.id === layerId)
    );
    if (folder) {
      /* There should always be a folder, this if statement is because Typescript knows .find()
      could return undefined */
      const idx = folder.layers.findIndex((layer) => layer.id === layerId);
      get().replaceLayer({
        ...folder,
        layers: remove(folder.layers, idx),
      });
    }
  }
};

export const createRemoveFolder = (get: GetFunction) => (folder: Folder) => {
  const publication = get();
  /* Remove the folder from the publication and then add any layers that were inside it to the list outside of a folder. */
  const openMap = getOpenMap(publication);
  const filteredLayers = openMap.layers.filter((layer) => layer.id !== folder.id);
  publication.replaceMap({
    ...openMap,
    layers: [...filteredLayers, ...folder.layers],
  });
};

export const createReplaceLayer = (get: GetFunction) => (layer: Layer | Folder) => {
  const publication = get();
  const openMap = getOpenMap(publication);
  publication.replaceMap({
    ...openMap,
    layers: openMap.layers.map((oldLayer) => {
      if (oldLayer.id === layer.id) {
        return layer;
      }
      if (oldLayer.type === "folder") {
        const idx = oldLayer.layers.findIndex((sublayer) => sublayer.id === layer.id);
        if (idx !== -1) {
          return {
            ...oldLayer,
            layers: replaceElement(oldLayer.layers, idx, layer) as Layer[],
          };
        }
      }
      return oldLayer;
    }),
  });
};

export const createReplaceFile = (get: GetFunction, set: SetFunction) => (file: CompleteFile) => {
  const files = get().files;
  const fileIndex = files.findIndex((f) => f.path === file.path);
  if (fileIndex === -1) {
    console.warn(`File with path ${file.path} not found`);
    return;
  }
  const newFiles = [...files];
  newFiles[fileIndex] = file;
  set({ files: newFiles });
};

export const createChangeLayerName =
  (get: GetFunction) => (layer: Layer | Folder, name: string) => {
    get().replaceLayer({
      ...layer,
      name,
    });
  };

export const createChangeFileName = (get: GetFunction) => (file: CompleteFile) => {
  get().replaceFile(file);
};

export const createRemoveFileByPath =
  (get: GetFunction, set: SetFunction) => (filePath: string) => {
    const files = get().files;
    const newFiles = files.filter((file) => file.path !== filePath);
    const maps = get().maps;
    const newMaps = maps.map((map) => ({
      ...map,
      layers: map.layers
        .filter((layer) => layer.type !== "layer" || layer.filePath !== filePath)
        .map((layer) =>
          layer.type === "folder"
            ? {
                ...layer,
                layers: layer.layers.filter(
                  (sublayer) => sublayer.type !== "layer" || sublayer.filePath !== filePath
                ),
              }
            : layer
        ),
    }));
    set({ files: newFiles, maps: newMaps });
  };

export const createPublishVersion = (set: SetFunction) => (version: number) =>
  set({ publishedVersion: version });

export const createToggleEditing =
  (get: GetFunction, set: SetFunction) =>
  (resetPublicationVersion: (isChanged: boolean) => void) => {
    const publication = get();

    if (publication.isEditing) {
      resetPublicationVersion(getIsChanged(publication));
    } else {
      set({ isEditing: true });
    }
  };
